import React, { FC, useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  BrandCategory,
  BrandsOfStores,
  Brand,
  fetchAllBrands,
} from '../../../fetch/fetchBrands';
import { UsersMenu } from '../UsersMenu';
import { BrandFormModal } from './BrandFormModal';
import './Brands.scss';
import Button from '../../../components/buttons/Button';
import Alert from '../../../components/alerts/Alert';
import { FiEdit3 } from 'react-icons/fi';

import Table, {
  TableColumns,
  CollapsibleOptions,
} from '../../../components/table/Table';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';

export const Brands: FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BrandsOfStores[]>([]);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [brandToEdit, setBrandToEdit] = useState<Brand>(null);

  const onNewCreate = () => {
    setShowCreateModal(true);
  };

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    const data = await fetchAllBrands();
    if (data == null) {
      setError('Could not fetch brands');
    } else {
      setData(data.brandsOfStores);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const columns: TableColumns[] = [
    {
      key: 'name',
      header: 'Store',
      align: 'left',
      sortable: true,
    },
    {
      key: 'brands',
      header: 'Brands',
      sortable: true,
      sorter: (a: Brand[], b: Brand[]) => a.length - b.length,
      formatter: (data: Brand[]) => {
        return data.length > 0 ? data.map(b => b.name).toString() : '';
      },
    },
  ];

  const collapsible: CollapsibleOptions = {
    idProperty: 'id',
    columns: [
      {
        key: 'name',
        header: 'Brand name',
      },
      {
        key: 'url',
        header: 'Url',
      },
      {
        key: 'categories',
        header: 'Categories',
        formatter: (brand: Brand) =>
          brand.categories.map(cat => (
            <span key={cat} className="category-label">
              {BrandCategory[cat]}
            </span>
          )),
      },
      {
        key: 'logo',
        header: 'Logo',
        align: 'right',
        formatter: (brand: Brand) =>
          brand.logoUrl !== '' ? (
            <img className="logo" src={`${brand.logoUrl}?h=60`} alt="" />
          ) : (
            <img src="https://via.placeholder.com/468x60" alt="" />
          ),
      },
      {
        key: 'actions',
        header: 'Actions',
        formatter: (brand: Brand) => (
          <ButtonComponent
            startIcon={<FiEdit3 />}
            text="Edit"
            variant="text"
            onClick={() => {
              setBrandToEdit(brand);
              setShowEditModal(true);
            }}
          />
        ),
      },
    ] as TableColumns[],
    getRowData: (row: any) => row.brands,
  };

  return (
    <>
      <Helmet>
        <title>Users - Brands</title>
      </Helmet>
      <UsersMenu />
      <section className="brand-list">
        <div className="brand--list__container">
          <div className="brand-create-button">
            <Button title="Create" onClick={() => onNewCreate()} />
          </div>
          <Table
            idProperty="accountId"
            loading={loading}
            defaultSort={{ sortKey: 'name', sortOrdering: 'asc' }}
            data={data}
            columns={columns}
            collapsible={collapsible}
          />
          {error && <Alert type="danger">{error}</Alert>}
        </div>
        <div>
          {showCreateModal && (
            <BrandFormModal
              onCloseModal={created => {
                setShowCreateModal(false);
                if (created) {
                  fetchBrands();
                }
              }}
            />
          )}
          {showEditModal && (
            <BrandFormModal
              brand={brandToEdit}
              onCloseModal={updated => {
                setShowEditModal(false);
                if (updated) {
                  fetchBrands();
                }
              }}
            />
          )}
        </div>
      </section>
    </>
  );
};
